'use client'

import React, { FC, ReactElement } from 'react'
import styles from './CollectionsFeed.module.css'
import Loading from '@/components/circle-loading'
import InfiniteScroll from 'react-infinite-scroll-component'
import FeedHeader from '@/components/feed-header'
import ItemTable from '@/components/item-table'
import RuneRow from '@/components/rune-row'
import classNames from 'classnames'
import { Tab } from '@/components/tabs'
import { useRouter } from 'next/navigation'
import useRunes from '@/utils/hooks/useRunes'
import Fuse from 'fuse.js'

let INITIAL_RENDER = true

const _DEFAULT_HEADER = '7D Volume'
const _INFINITE_LIMIT = 100

const CollectionsFeed: FC<{
	tabs?: Tab[]
	items: any[]
	title: string
	isMobile?: boolean
	className?: string
	sns?: boolean
	infinite?: boolean
}> = (props): ReactElement => {
	const router = useRouter()

	const { className, isMobile: _isMobile, title, tabs, sns, infinite: _infinite } = props
	const [search, setSearch] = React.useState('')
	const [selected, setSelected] = React.useState('IdAsc')
	const [gridView, setGridView] = React.useState(false)
	const [size, setSize] = React.useState(1)

	const { data, isLoading } = useRunes()

	const items: any[] = React.useMemo(() => {
		if (!data?.length) {
			return []
		}

		let res = data

		if (search) {
			const fuseOptions = {
				isCaseSensitive: false,
				includeScore: false,
				shouldSort: true,
				includeMatches: false,
				findAllMatches: false,
				minMatchCharLength: 1,
				location: 0,
				threshold: 0.35,
				distance: 100,
				useExtendedSearch: false,
				ignoreLocation: false,
				ignoreFieldNorm: false,
				fieldNormWeight: 1,
				keys: ['id', 'name']
			}

			const f = new Fuse(res, fuseOptions)
			res = f.search(search).map((e) => e.item)
			console.log('search', res)
		}

		if (selected === 'IdAsc') {
			res = res.sort((a, b) => a.num - b.num)
		}

		if (selected === 'IdDesc') {
			res = res.sort((a, b) => b.num - a.num)
		}

		if (selected === 'OwnersDesc') {
			res = res.sort((a, b) => b.owners - a.owners)
		}

		if (!search) {
			return res.slice(0, (size + 1) * 36)
		}

		return res
	}, [selected, data, size, search])

	const setFilter = (key: any) => {
		INITIAL_RENDER = false
		setSelected(key)
		setSize(1)
		router.replace(`${window.location.pathname}?s=${key}`)
	}

	const filters = [
		{
			title: 'Oldest',
			id: 'IdAsc',
			onClick: () => setFilter('IdAsc')
		},
		{
			title: 'Newest',
			id: 'IdDesc',
			onClick: () => setFilter('IdDesc')
		},
		{
			title: 'Owners',
			id: 'OwnersDesc',
			onClick: () => setFilter('OwnersDesc')
		}
	]

	const filter = filters.find((e) => e.id === selected)

	const columnHeaders = [
		{ label: 'RUNE' },
		{ label: 'SUPPLY' },
		{ label: 'OWNERS' },
		{ label: 'MINT' }
	]

	const renderItemTable = () => {
		const key = (filter as any).id

		return (
			<ItemTable
				className={styles.table}
				columns={columnHeaders}
				headerClassName={styles.header}
				columnClassName={styles.column}
				isLoading={isLoading && !INITIAL_RENDER}
			>
				{items?.map((e, i) => (
					<RuneRow
						key={i}
						collection={e}
						selectedKey={key as any}
						selectedTitle={(filter as any).title}
					/>
				))}
			</ItemTable>
		)
	}

	return (
		<div className={classNames({ [props.className as any]: !!className }, styles.root)}>
			<FeedHeader
				title={title}
				mobileTitle
				selectedOption={selected}
				options={filters}
				gridView={gridView}
				setGridView={setGridView}
				search={sns ? undefined : search}
				setSearch={sns ? undefined : setSearch}
				tabs={tabs}
				label={sns ? 'SNS' : 'Collection'}
				divider
			/>
			<InfiniteScroll
				dataLength={items?.length}
				next={() => setSize((prev) => prev + 1)}
				hasMore={!search && items?.length < data?.length}
				loader={
					<div className={styles.loading}>
						<Loading />
					</div>
				}
			>
				{renderItemTable()}
			</InfiniteScroll>
		</div>
	)
}

export default CollectionsFeed
